import {
  Box,
  SpaceBetween,
  Button,
  StatusIndicator,
  Header,
  Checkbox,
  Container,
  Alert,
  Pagination,
} from '@cloudscape-design/components';
import { useEffect, useState, memo, useCallback } from 'react';
import { Permission } from 'src/types/orbit/Permission';
import Divider from '../divider/Divider';
import { convertList, filterArrayByArray } from 'src/utils/generalUtils';
import { differenceBy } from 'lodash';
import { PaginationConfig } from 'src/contstant/PaginationConfig';
import _ from 'lodash';

interface PermissionListProps {
  title: string;
  permissions: Permission[];
  selectedOldPermissions?: string[];
  excludePermissions?: string[];
  isLoading: boolean;
  footerEnabled?: boolean;
  disableSelection?: boolean;
  footerButtonLoader?: boolean;
  readOnly?: boolean;
  isLocalPagination?: boolean;
  showSelectedPermissionsOnly?: boolean;
  error: string | null;
  paginationComponent?: React.ReactNode;
  onChangePermission: (selectedPermissions: string[]) => void;
  onSaveClick?: (selectedPermissions: string[]) => void;
}

const PermissionCheckbox = memo(
  ({
    permission,
    isChecked,
    disabled,
    readOnly,
    onChange,
  }: {
    permission: Permission;
    isChecked: boolean;
    disabled: boolean;
    readOnly: boolean;
    onChange: (checked: boolean) => void;
  }) => (
    <Checkbox
      data-testid="permission-checkbox"
      disabled={disabled}
      checked={isChecked}
      readOnly={readOnly}
      onChange={({ detail }) => onChange(detail.checked)}
    >
      {permission.name}
    </Checkbox>
  ),
);

const PermissionList = ({
  title,
  permissions,
  isLoading,
  footerEnabled = true,
  disableSelection = false,
  readOnly = false,
  showSelectedPermissionsOnly = false,
  footerButtonLoader,
  selectedOldPermissions,
  paginationComponent,
  onChangePermission,
  onSaveClick,
  isLocalPagination = true,
  excludePermissions,
  error,
}: PermissionListProps) => {
  const [selectedPermissionsList, setSelectedPermissionsList] = useState<Permission[]>(
    [],
  );
  const [finalPermissionList, setFinalPermissionList] = useState<Permission[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const itemsPerPage = PaginationConfig.PERMISSIONS_MAX_RESULT;

  useEffect(() => {
    const newList = _.filter(
      permissions,
      (item) => !_.includes(excludePermissions, item.permissionId),
    );

    const filteredList = showSelectedPermissionsOnly
      ? filterArrayByArray(newList, selectedOldPermissions || [], 'permissionId')
      : newList;

    setFinalPermissionList(filteredList);
    setSelectedPermissionsList(getDefaultSelectedPermission());
  }, [
    permissions,
    selectedOldPermissions,
    showSelectedPermissionsOnly,
    excludePermissions,
  ]);
  //reset current page
  useEffect(() => {
    setCurrentPageIndex(1);
  }, [finalPermissionList]);

  const getDefaultSelectedPermission = (): Permission[] => {
    return selectedOldPermissions?.length
      ? filterArrayByArray(permissions, selectedOldPermissions, 'permissionId')
      : [];
  };

  const handleCheckboxChange = useCallback(
    (permission: Permission, checked: boolean) => {
      setSelectedPermissionsList((prev) => {
        const newList = checked
          ? [...prev, permission]
          : prev.filter((p) => p.permissionId !== permission.permissionId);

        onChangePermission(
          convertList<Permission, string>(newList, (item) => item.permissionId),
        );
        return newList;
      });
    },
    [onChangePermission],
  );

  const renderContent = () => {
    if (isLoading) {
      return <StatusIndicator type="loading">Loading ...</StatusIndicator>;
    }

    if (!finalPermissionList.length) {
      return <StatusIndicator type="warning">No permissions.</StatusIndicator>;
    }

    const items = isLocalPagination
      ? finalPermissionList.slice(
          (currentPageIndex - 1) * itemsPerPage,
          currentPageIndex * itemsPerPage,
        )
      : finalPermissionList;

    return items.map((permission, index) => (
      <div key={`${permission.permissionId}=${title}`}>
        <PermissionCheckbox
          permission={permission}
          readOnly={readOnly}
          isChecked={selectedPermissionsList.some(
            (p) => p.permissionId === permission.permissionId,
          )}
          disabled={disableSelection}
          onChange={(checked) => handleCheckboxChange(permission, checked)}
        />
        {index !== items.length - 1 && <Divider />}
      </div>
    ));
  };

  const hasPermissionChanges = useCallback(() => {
    if (!selectedOldPermissions) return false;

    const currentPermissionIds = selectedPermissionsList.map((p) => p.permissionId);

    // First check if lengths are different
    if (currentPermissionIds.length !== selectedOldPermissions.length) {
      return true;
    }

    // Check if arrays have the same elements (order doesn't matter)
    const differences1 = differenceBy(
      currentPermissionIds,
      selectedOldPermissions,
      String,
    );
    const differences2 = differenceBy(
      selectedOldPermissions,
      currentPermissionIds,
      String,
    );

    // If either array has unique elements, they're different
    return differences1.length > 0 || differences2.length > 0;
  }, [selectedOldPermissions, selectedPermissionsList]);

  return (
    <Container
      data-testid="permission-container"
      header={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box fontSize="body-m" fontWeight="bold">
            {title}
          </Box>
          {isLocalPagination ? (
            <Pagination
              currentPageIndex={currentPageIndex}
              pagesCount={Math.ceil(finalPermissionList.length / itemsPerPage) || 1}
              onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
            />
          ) : (
            paginationComponent
          )}
        </div>
      }
    >
      {renderContent()}
      {footerEnabled && (
        <Box margin={{ top: 'xl' }}>
          <SpaceBetween size="s" alignItems="end" direction="horizontal">
            <Button
              data-testid="save-button"
              variant="primary"
              loading={footerButtonLoader}
              disabled={!hasPermissionChanges()}
              onClick={() =>
                onSaveClick?.(
                  convertList<Permission, string>(
                    selectedPermissionsList,
                    (item) => item.permissionId,
                  ),
                )
              }
            >
              Save
            </Button>
            <Button
              data-testid="cancel-button"
              variant="normal"
              disabled={!hasPermissionChanges()}
              onClick={() => setSelectedPermissionsList(getDefaultSelectedPermission())}
            >
              Cancel
            </Button>
          </SpaceBetween>
        </Box>
      )}
      {error && (
        <Box margin={{ top: 'xxxl', bottom: 'xxxl' }}>
          <Alert type="error" header={error} />
        </Box>
      )}
    </Container>
  );
};

export default memo(
  PermissionList,
  (prev, next) =>
    prev.permissions === next.permissions &&
    prev.excludePermissions === next.excludePermissions &&
    prev.isLoading === next.isLoading &&
    prev.selectedOldPermissions === next.selectedOldPermissions &&
    prev.footerEnabled === next.footerEnabled &&
    prev.disableSelection === next.disableSelection &&
    prev.footerButtonLoader === next.footerButtonLoader &&
    prev.readOnly === next.readOnly &&
    prev.showSelectedPermissionsOnly === next.showSelectedPermissionsOnly &&
    prev.error === next.error,
);
